import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  MessageSquare, Search, ChevronRight, X, Mail, Building2, User,
  CheckCircle2, Clock, Trash2, ExternalLink
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContactRequests = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [searchTerm, statusFilter, contacts]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setContacts(response.data);
      setFilteredContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = contacts;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.company?.toLowerCase().includes(term) ||
        c.message?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => (c.status || 'new') === statusFilter);
    }
    
    setFilteredContacts(filtered);
  };

  const openDetail = (contact) => {
    setSelectedContact(contact);
    setDetailModalOpen(true);
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await axios.put(`${API}/contact/${contactId}/status`, { status: newStatus });
      fetchContacts();
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, status: newStatus });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Fehler beim Aktualisieren des Status');
    }
  };

  const handleDeleteClick = (contactId, e) => {
    e?.stopPropagation();
    setDeleteConfirmId(contactId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    setDeletingId(deleteConfirmId);
    try {
      await axios.delete(`${API}/contact/${deleteConfirmId}`);
      setDeleteConfirmId(null);
      if (selectedContact?.id === deleteConfirmId) {
        setDetailModalOpen(false);
        setSelectedContact(null);
      }
      fetchContacts();
    } catch (error) {
      console.error('Failed to delete contact:', error);
      alert('Fehler beim Löschen');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: 'Neu', bg: 'bg-lilac', text: 'text-ink', icon: Clock },
      in_progress: { label: 'In Bearbeitung', bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      completed: { label: 'Erledigt', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 }
    };
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-none ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const newCount = contacts.filter(c => !c.status || c.status === 'new').length;

  return (
    <div className="space-y-6" data-testid="admin-contact-requests">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink flex items-center gap-3">
            <MessageSquare className="w-7 h-7 text-purple" />
            Kontaktanfragen
            {newCount > 0 && (
              <span className="px-2 py-0.5 text-sm font-medium bg-lilac text-ink rounded-none">
                {newCount} neu
              </span>
            )}
          </h1>
          <p className="text-ink/60 mt-1">Anfragen über das Kontaktformular</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ink/60" />
          <Input
            placeholder="Suchen nach Name, E-Mail, Unternehmen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 rounded-none"
            data-testid="contact-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'new', 'in_progress', 'completed'].map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              onClick={() => setStatusFilter(status)}
              className={`rounded-none ${statusFilter === status ? 'bg-purple text-white' : ''}`}
              size="sm"
            >
              {status === 'all' ? 'Alle' : 
               status === 'new' ? 'Neu' :
               status === 'in_progress' ? 'In Bearbeitung' : 'Erledigt'}
            </Button>
          ))}
        </div>
      </div>

      {/* Contact List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-purple border-t-transparent rounded-none mx-auto"></div>
          <p className="mt-4 text-ink/60">Lade Kontaktanfragen...</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="text-center py-12 bg-off rounded-none">
          <MessageSquare className="w-12 h-12 text-ink/60 mx-auto mb-4" />
          <p className="text-ink/60">Keine Kontaktanfragen gefunden</p>
        </div>
      ) : (
        <div className="bg-white rounded-none border-2 border-ink overflow-hidden">
          {filteredContacts.map((contact, index) => (
            <div
              key={contact.id || index}
              onClick={() => openDetail(contact)}
              className="p-4 hover:bg-off cursor-pointer border-b border-ink/15 last:border-b-0 transition-colors"
              data-testid={`contact-row-${index}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 bg-purple rounded-none flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-ink">{contact.name}</p>
                    <p className="text-sm text-ink/60 flex items-center gap-2 flex-wrap">
                      {contact.company && (
                        <>
                          <Building2 className="w-3 h-3" />
                          <span>{contact.company}</span>
                          <span>•</span>
                        </>
                      )}
                      <Mail className="w-3 h-3" />
                      <span>{contact.email}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap sm:justify-end">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm text-ink/80 line-clamp-1 max-w-xs">
                      {contact.message?.substring(0, 50)}...
                    </p>
                    <p className="text-xs text-ink/60">
                      {new Date(contact.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  {getStatusBadge(contact.status || 'new')}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteClick(contact.id, e)}
                    className="text-ink/60 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <ChevronRight className="w-5 h-5 text-ink/60" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {detailModalOpen && selectedContact && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-ink">
                  Kontaktanfrage von {selectedContact.name}
                </h2>
                <p className="text-ink/60">{selectedContact.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteClick(selectedContact.id)}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Löschen
                </Button>
                <button onClick={() => setDetailModalOpen(false)} className="text-ink/60 hover:text-ink/80">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-off rounded-none">
                  <p className="text-sm text-ink/60 mb-1">Name</p>
                  <p className="font-medium text-ink">{selectedContact.name}</p>
                </div>
                <div className="p-4 bg-off rounded-none">
                  <p className="text-sm text-ink/60 mb-1">Unternehmen</p>
                  <p className="font-medium text-ink">{selectedContact.company || '-'}</p>
                </div>
                <div className="p-4 bg-off rounded-none">
                  <p className="text-sm text-ink/60 mb-1">E-Mail</p>
                  <a href={`mailto:${selectedContact.email}`} className="font-medium text-purple hover:underline flex items-center gap-1">
                    {selectedContact.email}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="p-4 bg-off rounded-none">
                  <p className="text-sm text-ink/60 mb-1">Datum</p>
                  <p className="font-medium text-ink">
                    {new Date(selectedContact.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              {/* Message */}
              <div>
                <h3 className="font-semibold text-ink mb-3">Nachricht</h3>
                <div className="p-4 bg-off rounded-none">
                  <p className="text-ink/80 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>

              {/* Status */}
              <div>
                <h3 className="font-semibold text-ink mb-3">Status ändern</h3>
                <div className="flex gap-2">
                  {[
                    { value: 'new', label: 'Neu', color: 'blue' },
                    { value: 'in_progress', label: 'In Bearbeitung', color: 'yellow' },
                    { value: 'completed', label: 'Erledigt', color: 'green' }
                  ].map((status) => (
                    <Button
                      key={status.value}
                      variant={(selectedContact.status || 'new') === status.value ? 'default' : 'outline'}
                      onClick={() => handleStatusChange(selectedContact.id, status.value)}
                      className={`rounded-none ${
                        (selectedContact.status || 'new') === status.value 
                          ? status.color === 'green' ? 'bg-green-600' : 
                            status.color === 'yellow' ? 'bg-yellow-500' : 'bg-purple'
                          : ''
                      }`}
                      size="sm"
                    >
                      {status.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-ink/15">
                <a
                  href={`mailto:${selectedContact.email}?subject=Re: Deine Anfrage bei gastro-beratung.de`}
                  className="inline-flex items-center gap-2 bg-purple hover:bg-deep text-white px-4 py-2 rounded-none transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Per E-Mail antworten
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-ink/70 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">Kontaktanfrage löschen</h2>
              <button onClick={() => setDeleteConfirmId(null)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-none flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-ink">
                    {contacts.find(c => c.id === deleteConfirmId)?.name}
                  </p>
                  <p className="text-sm text-ink/60">
                    {contacts.find(c => c.id === deleteConfirmId)?.email}
                  </p>
                </div>
              </div>
              <p className="text-ink/80 bg-red-50 p-4 rounded-none border border-red-100">
                Diese Kontaktanfrage wird unwiderruflich gelöscht.
              </p>
            </div>
            
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="flex-1 rounded-none">
                Abbrechen
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deletingId}
                className="btn--danger flex-1"
              >
                {deletingId ? 'Wird gelöscht...' : 'Löschen'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContactRequests;
